import React, { useEffect, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import { variance } from 'mathjs';
import { Checkbox, Container, Col, Label, Div, Row, Text } from '@pinpoint/design-kit';

import Radar from 'src/charts/radar';
import PxHistogram from 'src/charts/px_histogram';

import { pxScoresPropType, stylePropType, pxHistogramsPropType } from 'src/PropTypes';
import { Style1 as Style } from 'src/styles';
import { EventEmitter, withFixedTitle } from 'src/components';

const eventEmitter = new EventEmitter();

const SquishlessRadar = withFixedTitle(Radar);

/* eslint-disable jsx-a11y/mouse-events-have-key-events */
/* eslint-disable react/prop-types */
function PxHistogramContent({ pxHistogramsList, theme, onThemeChange, trait: initialTrait, defaultTheme }) {
  const [selectedTrait, setSelectedTrait] = useState(initialTrait);
  eventEmitter.subscribe('selectedTrait', (trait) => {
    if (trait) {
      setSelectedTrait(trait);
    }
  });
  const options = {
    legend: {
      top: 'bottom',
      padding: [0, 0, 30, 0],
      textStyle: {
        fontSize: 16,
      },
      itemGap: 24,
    },
    grid: {
      top: '23%',
      bottom: '20%',
    },
  };

  useEffect(() => {
    setSelectedTrait(initialTrait);
  }, [initialTrait]);

  const hasHistograms =
    pxHistogramsList.filter(
      ({ pxHistograms }) =>
        pxHistograms && pxHistograms.length && pxHistograms.find(({ trait }) => trait.code === selectedTrait.code)
    ).length === pxHistogramsList.length;

  if (!hasHistograms) {
    return null;
  }

  const series = pxHistogramsList.map(({ name, theme: segmentTheme, pxHistograms }) => ({
    name,
    theme: segmentTheme,
    values: pxHistograms.find(({ trait }) => trait.code === selectedTrait.code).frequencies,
  }));

  if (series.length === 1) {
    const { refresh } = pxHistogramsList[0];
    if (refresh && refresh.pxHistograms && refresh.pxHistograms.length) {
      const populationHistogram = pxHistogramsList[0].refresh.pxHistograms.find(
        ({ trait }) => trait.code === selectedTrait.code
      );
      series.push({
        name: 'US Population',
        values: populationHistogram.frequencies,
      });
      options.series = [
        {},
        {
          itemStyle: {
            color: '#A0A0A0',
          },
        },
      ];
    }
  }
  return (
    <Div w="100%" h="100%">
      <PxHistogram
        theme={theme}
        defaultTheme={defaultTheme}
        series={series}
        onThemeChange={(nextTheme) => {
          onThemeChange(nextTheme);
        }}
        options={options}
      />
    </Div>
  );
}

function TextContent({ trait: initialTrait, single }) {
  const [selectedTrait, setSelectedTrait] = useState(initialTrait);
  const [values, setValues] = useState(null);
  eventEmitter.subscribe('selectedTrait', (trait) => {
    if (trait) {
      setSelectedTrait(trait);
    }
  });
  eventEmitter.subscribe('traitValues', (values_) => {
    if (values_) {
      setValues(values_);
    }
  });
  const { trait, highRangeName, lowRangeName, description } = selectedTrait;

  useEffect(() => {
    setSelectedTrait(initialTrait);
  }, [initialTrait]);

  return (
    <Div d="flex" flexDir="column" justify="flex-start" align="left" overflow="auto" w="100%">
      <Text textSize="title" fontFamily="secondary" textColor="tabSelected" m={{ b: '1rem' }}>
        {values !== null && single ? (values[0] < 50 ? lowRangeName : highRangeName) : trait}
      </Text>
      <Text textSize="subheader" textColor="tabSelected" style={{ lineHeight: '1.4' }}>
        {description}
      </Text>
    </Div>
  );
}

function RadarContent({ labels, series, style, ...rest }) {
  return (
    <SquishlessRadar
      title=""
      labels={labels}
      series={series}
      style={style}
      onMouseover={({ label, values }) => {
        eventEmitter.dispatch('selectedTrait', label);
        eventEmitter.dispatch('traitValues', values);
      }}
      {...rest}
    />
  );
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'check': {
      return {
        ...state,
        checked: [...state.checked, action.payload],
      };
    }
    case 'uncheck': {
      return {
        ...state,
        checked: state.checked.filter((index) => index !== action.payload),
      };
    }
    case 'toggle': {
      const isPresent = state.checked.includes(action.payload);
      const newChecked = isPresent
        ? state.checked.filter((index) => index !== action.payload)
        : [...state.checked, action.payload];
      if (newChecked.length === 0) return state;
      return {
        ...state,
        checked: newChecked,
      };
    }
    default:
      return state;
  }
};

function Segments({ objects: _objects, style, minW, minH, theme: themeProp, defaultTheme, onThemeChange }) {
  const [theme, setTheme] = useState(themeProp);
  const initialState = {
    checked: _objects.map((_, index) => index),
  };
  const [state, dispatch] = useReducer(reducer, initialState);
  if (!_objects) {
    return <Div>No Data</Div>;
  }
  const objects = [];
  const segments = [];
  _objects.forEach(({ pxScores, ...rest }) => {
    const pxScoresCopy = pxScores.filter(({ trait: { subType } }) => subType !== 'factor' && subType !== 'redacted');
    pxScoresCopy.sort(({ trait: { code: codeA } }, { trait: { code: codeB } }) => (codeA > codeB ? 1 : -1));
    objects.push({
      ...rest,
      pxScores: pxScoresCopy,
    });
  });

  const traits = objects[0].pxScores.map(({ trait }) => trait);
  const selectedObjects = objects.filter((_, index) => state.checked.includes(index));
  for (let i = 0; i < traits.length; i += 1) {
    if (traits[i].subType !== 'factor' && traits[i].subType !== 'redacted') {
      const scores = selectedObjects.map(({ pxScores }) => pxScores[i].score);
      segments.push({
        trait: traits[i],
        scores,
        order: scores.length > 1 ? variance(...scores) || 0.01 : Math.abs(50 - scores[0]),
      });
    }
  }
  segments.sort(({ order: orderA }, { order: orderB }) => orderB - orderA);

  // Isolate the labels
  const labels = segments.map(({ trait }) => trait);
  const series = [];
  for (let i = 0; i < selectedObjects.length; i += 1) {
    series.push({
      name: selectedObjects[i].name,
      values: segments.map(({ scores }) => scores[i]),
      theme: selectedObjects[i].theme || 'pinpoint',
    });
  }
  const top7Labels = labels.slice(0, 7);
  const top7Series = series.map(({ name, theme: segmentTheme, values }) => ({
    name,
    theme: segmentTheme,
    values: values.slice(0, 7),
    label: {
      fontSize: 16,
    },
  }));

  const pxHistogramsList = selectedObjects.map(({ pxHistograms, theme: segmentTheme, name, refresh }) => ({
    name,
    theme: segmentTheme,
    pxHistograms,
    refresh,
  }));

  return (
    <Container h="100%" minH={minH} w="100%" p={{ x: '1rem' }} minW={minW}>
      <Row w="100%" h="100%">
        <Col size={{ xs: '12', lg: '7' }} h={{ xs: '100%', lg: '100%' }} gutterWidth="1.25rem">
          <Div
            pos="relative"
            minH="800px"
            h="100%"
            w="100%"
            d="flex"
            justify="flex-start"
            align="center"
            flexDir="column"
            bg="white"
            p={{ x: '1.5rem', y: '1.25rem' }}
            rounded="md"
          >
            <Div
              pos="absolute"
              d="flex"
              align="center"
              justify="flex-start"
              top="6rem"
              left="2rem"
              m={{ x: '-2rem' }}
              zIndex="999"
            >
              {objects.map((object, index) => (
                <Div p={{ x: '2rem', y: '1rem' }}>
                  <Label align="center" textSize="1rem">
                    <Checkbox
                      checked={state.checked.includes(index)}
                      data-tip={state.checked.includes(index) ? 'Hide' : 'Show'}
                      onChange={() => {
                        dispatch({
                          type: 'toggle',
                          payload: index,
                        });
                      }}
                    />
                    &nbsp; &nbsp;
                    {object.name}
                  </Label>
                </Div>
              ))}
            </Div>
            <RadarContent
              title={
                state.checked.length > 1
                  ? 'Top 7 Divergent Traits Between Datasets'
                  : 'Top 7 Divergent Traits With US Population'
              }
              labels={top7Labels}
              series={top7Series}
              style={style}
              showRangeControl
              relative
              theme={theme}
              defaultTheme={defaultTheme}
              onThemeChange={(nextTheme) => {
                setTheme(nextTheme);
                onThemeChange(_objects[state.checked[0]].id, nextTheme);
              }}
              options={{
                radar: {
                  radius: '74%',
                  center: ['50%', '53%'],
                  nameGap: 20,
                },
              }}
            />
          </Div>
        </Col>
        <Col size={{ xs: '12', lg: '5' }} h={{ xs: '100%', lg: '100%' }} gutterWidth="1.25rem">
          <Div
            minH="100%"
            h="100%"
            w="100%"
            d="flex"
            justify="flex-start"
            align="center"
            flexDir="column"
            bg="white"
            p={{ x: '1.5rem', y: '1.25rem' }}
            rounded="md"
          >
            <Div
              h="13rem"
              w="100%"
              minH="13rem"
              bg="secondary"
              d="flex"
              justify="flex-start"
              align="center"
              flexDir="column"
              p={{ x: '1.25rem', y: '1rem' }}
              m={{ b: '1.25rem' }}
              rounded="md"
              data-test="desc-div"
            >
              <TextContent trait={labels[0]} single={state.checked.length <= 1} />
            </Div>
            <Div h="100%" w="100%" d="flex" justify="flex-start" align="center" flexDir="column" data-test="hist-div">
              <PxHistogramContent
                pxHistogramsList={pxHistogramsList}
                theme={theme}
                defaultTheme={defaultTheme}
                onThemeChange={(nextTheme) => {
                  setTheme(nextTheme);
                  onThemeChange(_objects[state.checked[0]].id, nextTheme);
                }}
                trait={labels[0]}
              />
            </Div>
          </Div>
        </Col>
      </Row>
    </Container>
  );
}

Segments.propTypes = {
  object: PropTypes.shape({
    name: PropTypes.string,
    pxScores: pxScoresPropType,
    pxHistograms: pxHistogramsPropType,
  }).isRequired,
  style: stylePropType,
  styleOverride: PropTypes.shape({}),
  minH: PropTypes.string,
  minW: PropTypes.string,
  theme: PropTypes.string,
  onThemeChange: PropTypes.func,
};

Segments.defaultProps = {
  style: Style,
  styleOverride: {
    background: Style.global.backgroundColor,
  },
  minH: '100vh',
  minW: '100vw',
  theme: 'pinpoint',
  onThemeChange: () => null,
};

export default Segments;
